import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Markdown from "react-markdown";
import { useChatEngine } from "../core/useChatEngine";
import { MessageType, MessageStatus } from "../core/types/messages";
import ElementPicker from "../../components/AIChat/ElementPicker";
import ToolGroup from "./ToolGroup";
import {
  useAttachments,
  AttachmentStatus,
  ACCEPTED_IMAGE_TYPES,
} from "./useAttachments";
import { getUserProfile } from "../../services/api";
import { isFreeOrTinyUser } from "../../utils/general";
import "../../components/AIChat/AIChat.css";
import "./ToolMessage.css";
import {PickIcon} from "../../components/layout/panelIcons";

// Within this distance of the bottom the chat still counts as "at the bottom"
const AT_BOTTOM_PX = 24;

function AIChat(
  {
    domain,
    filename,
    fileList,
    setFileList,
    dbRef,
    iframeRef,
    saveFile,
    onPickerStateChange,
    onFilesChanged,
    onPaywallRequired,
    sidePanel = false,
  },
  ref,
) {
  const [isOpen, setIsOpen] = useState(sidePanel);
  const [isClosing, setIsClosing] = useState(false);
  const [input, setInput] = useState("");
  const [pickerActive, setPickerActive] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const {
    loading,
    selectedElement,
    displayMessages,
    initializeChat,
    handleSubmit,
    handleRegenerate,
    handleClearChat,
    handleElementPicked,
    clearSelectedElement,
  } = useChatEngine({
    domain,
    fileList,
    setFileList,
    dbRef,
    iframeRef,
    saveFile,
    onFilesChanged,
  });

  // Client pre-check is UX-only; plan/rate-limit enforcement is server-authoritative.
  // If the profile call itself cannot be made, let the action through: blocking on
  // a check that is not the gate only punishes a bad connection, and the server
  // still refuses what is not allowed.
  const requirePaidPlan = useCallback(async () => {
    let profile;
    try {
      profile = await getUserProfile();
    } catch (err) {
      return true;
    }
    if (isFreeOrTinyUser(profile)) {
      onPaywallRequired?.(profile);
      return false;
    }
    return true;
  }, [onPaywallRequired]);

  const {
    attachments,
    readyAttachments,
    pending,
    isFull,
    isDragging,
    fileInputRef,
    dragProps,
    openFilePicker,
    onFileInputChange,
    onPaste,
    retryAttachment,
    removeAttachment,
    clearAttachments,
  } = useAttachments({ domain, requirePaidPlan });

  useEffect(() => {
    if (isOpen) {
      initializeChat();
    }
  }, [isOpen, initializeChat]);

  const messagesRef = useRef(null);
  const [atBottom, setAtBottom] = useState(true);

  const onMessagesScroll = () => {
    const el = messagesRef.current;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight <= AT_BOTTOM_PX);
  };

  const scrollToBottom = () => {
    const el = messagesRef.current;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // Follow new messages unless the user scrolled up to read; their own message always brings them back down
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    const last = displayMessages[displayMessages.length - 1];
    if (atBottom || last?.type === MessageType.USER) {
      el.scrollTop = el.scrollHeight;
    }
  }, [displayMessages, loading, atBottom]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() && !readyAttachments.length) return;
    if (loading || isCheckingAccess || pending) return;

    setIsCheckingAccess(true);
    try {
      if (!(await requirePaidPlan())) return;

      const userInput = input.trim();
      const sent = readyAttachments;
      // Both are cleared before the await, or the chip sits by the input for the
      // whole response while the text next to it has already gone.
      setInput("");
      clearAttachments();
      await handleSubmit(userInput, sent);
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const onElementPicked = (selector, html) => {
    setPickerActive(false);
    handleElementPicked(selector, html);
    if (onPickerStateChange) {
      onPickerStateChange(false);
    }
  };

  const onPickerClick = () => {
    setPickerActive(true);
    if (onPickerStateChange) {
      onPickerStateChange(true);
    }
  };

  const onPickerCancel = () => {
    setPickerActive(false);
    if (onPickerStateChange) {
      onPickerStateChange(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const onClearChat = async () => {
    if (!window.confirm("Clear chat history?")) return;
    clearAttachments();
    await handleClearChat();
  };

  useImperativeHandle(ref, () => ({
    openPicker: () => onPickerClick(),
    clearChat: () => onClearChat(),
  }));

  if (!isOpen && !isClosing) {
    if (sidePanel) return null;

    return (
      <button className="ai-chat-button" onClick={() => setIsOpen(true)}>
        AI
      </button>
    );
  }

  const getMessageClass = (msg) => {
    const classes = ["ai-chat-message"];
    // Map types to CSS classes
    const typeToClass = {
      [MessageType.USER]: "user",
      [MessageType.ASSISTANT]: "assistant",
      [MessageType.SYSTEM]: "system",
      [MessageType.ACTION]: "action",
    };
    classes.push(`ai-chat-message-${typeToClass[msg.type] || msg.type}`);
    if (msg.status === MessageStatus.ERROR) {
      classes.push("ai-chat-message-error");
    }
    return classes.join(" ");
  };

  const renderMessageContent = (msg) => {
    const text = msg.displayContent || msg.content;
    if (msg.type === MessageType.ASSISTANT) {
      return <Markdown>{text}</Markdown>;
    }
    return text;
  };

  const renderAvatar = (type) => {
    if (type === MessageType.ASSISTANT) {
      return (
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <rect width="32" height="32" rx="16" fill="#8657F3" />
          <path
            d="M20.9163 8.03613H17.9447H15.781H15.2307C14.7206 8.03613 14.2584 8.33675 14.0514 8.80274L10.8875 15.9263C10.7269 16.2877 10.9918 16.6949 11.3871 16.6949H14.7498L12.6291 22.8053C12.3667 23.5613 13.3902 24.0794 13.8444 23.4204L19.9841 14.5141C20.193 14.211 19.9765 13.7986 19.6084 13.7981L16.2108 13.7948L17.2477 11.4782C17.3763 11.1915 17.6612 11.007 17.9753 11.007H19.8087C20.1294 11.007 20.4201 10.8177 20.5501 10.5247L21.3552 8.71002C21.4957 8.39267 21.2634 8.03613 20.9163 8.03613Z"
            fill="white"
          />
        </svg>
      );
    }

    if (type === MessageType.USER) {
      return (
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <rect width="32" height="32" rx="16" fill="#E7E7E7" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.9992 12C12.9992 10.3431 14.3424 9 15.9992 9C17.6561 9 18.9992 10.3431 18.9992 12C18.9992 13.6569 17.6561 15 15.9992 15C14.3424 15 12.9992 13.6569 12.9992 12Z"
            fill="#3D3D3D"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.5001 21.4036C10.5516 18.4104 12.9939 16 15.9992 16C19.0047 16 21.447 18.4105 21.4984 21.4038C21.5018 21.6023 21.3875 21.784 21.207 21.8668C19.6211 22.5945 17.8569 23 15.9995 23C14.1418 23 12.3775 22.5945 10.7915 21.8666C10.611 21.7838 10.4967 21.6021 10.5001 21.4036Z"
            fill="#3D3D3D"
          />
        </svg>
      );
    }

    return null;
  };

  const lastMessage = displayMessages[displayMessages.length - 1];

  return (
    <>
      {pickerActive && (
        <ElementPicker
          iframeRef={iframeRef}
          onElementPicked={onElementPicked}
          onCancel={onPickerCancel}
        />
      )}

      <div
        className={`ai-chat-modal ${isClosing ? "closing" : ""}`}
        {...dragProps}
      >
        {isDragging && (
          <div className="ai-chat-drop-overlay">Drop images to attach</div>
        )}
        {!sidePanel && (
          <div className="ai-chat-header">
            <span>AI Assistant</span>
            <div className="ai-chat-header-buttons">
              <button
                className="ai-chat-icon-btn"
                onClick={onClearChat}
                disabled={loading || isCheckingAccess}
                title="Clear Chat"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
              <button
                className="ai-chat-icon-btn"
                onClick={handleClose}
                title="Close"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="ai-chat-messages-wrap">
          <div
            className="ai-chat-messages"
            ref={messagesRef}
            onScroll={onMessagesScroll}
          >
            {displayMessages.map((msg, idx) => {
              if (msg.type === MessageType.TOOL_GROUP) {
                return (
                  <div key={idx} className="ai-chat-message-row">
                    <ToolGroup
                      items={msg.items}
                      active={loading && idx === displayMessages.length - 1}
                    />
                  </div>
                );
              }

              const isUser = msg.type === MessageType.USER;
              const isAssistant = msg.type === MessageType.ASSISTANT;
              const isSystem = msg.type === MessageType.SYSTEM;

              return (
                <div
                  key={idx}
                  className={`ai-chat-message-row ${isUser ? "is-user" : isAssistant ? "is-assistant" : ""}`}
                >
                  {(isAssistant || isSystem) && (
                    <div className="ai-chat-message-avatar">
                      {renderAvatar(MessageType.ASSISTANT)}
                    </div>
                  )}

                  <div className={getMessageClass(msg)}>
                    {msg.attachments?.length > 0 && (
                      <div className="ai-chat-message-images">
                        {msg.attachments.map((image) => (
                          <img
                            key={image.url}
                            className="ai-chat-message-image"
                            src={image.url}
                            alt={image.name || "Attached image"}
                          />
                        ))}
                      </div>
                    )}
                    <div className="ai-chat-message-content">
                      {renderMessageContent(msg)}
                    </div>
                  </div>

                  {isUser && (
                    <div className="ai-chat-message-avatar">
                      {renderAvatar(MessageType.USER)}
                    </div>
                  )}

                  {msg.canRegenerate && msg.originalMessage && (
                    <div className="ai-chat-regenerate-section">
                      <span className="ai-chat-regenerate-hint">Try again?</span>
                      <button
                        className="ai-chat-regenerate-btn"
                        onClick={() => handleRegenerate(msg.originalMessage)}
                        disabled={loading || isCheckingAccess}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M9.34805 5.4534H12.2604V5.45236M1.74219 11.4594V8.54701M1.74219 8.54701L4.65456 8.54701M1.74219 8.54701L3.59771 10.4037C4.17551 10.9826 4.9088 11.4218 5.75565 11.6487C8.32296 12.3366 10.9618 10.8131 11.6497 8.24578M2.3527 5.75465C3.04061 3.18735 5.67948 1.66379 8.24679 2.3517C9.09365 2.57862 9.82694 3.01782 10.4047 3.59671L12.2604 5.45236M12.2604 2.54105V5.45236"
                            stroke="white"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Regenerate
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {loading && lastMessage?.type !== MessageType.TOOL_GROUP && (
              <div className="ai-chat-message-row">
                <ToolGroup items={[]} active />
              </div>
            )}
          </div>

          {!atBottom && (
            <button
              type="button"
              className="ai-chat-scroll-bottom"
              onClick={scrollToBottom}
              aria-label="Scroll to latest"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M7 2v10M2.5 7.5L7 12l4.5-4.5" />
              </svg>
            </button>
          )}
        </div>

        <form className="ai-chat-input-form" onSubmit={onSubmit}>
          {selectedElement && (
            <div className="ai-chat-selected-badge">
              <span className="ai-chat-selected-icon" aria-hidden>
                <svg
                  width="18"
                  height="21"
                  viewBox="0 0 18 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.8939 20.0214L10.5351 14.9503M10.5351 14.9503L8.02566 17.1756L8.59407 7.70584L13.8212 15.6227L10.5351 14.9503ZM3.01793 14.6832C-0.203894 11.4614 -0.203894 6.2378 3.01793 3.01598C6.23976 -0.205847 11.4634 -0.205847 14.6852 3.01598C16.2961 4.62685 17.1015 6.73815 17.1016 8.84946M5.13925 12.5619C3.089 10.5117 3.089 7.18755 5.13925 5.1373C7.1895 3.08705 10.5136 3.08705 12.5639 5.1373C13.589 6.16241 14.1015 7.50598 14.1016 8.84955"
                    stroke="#CC9A05"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="ai-chat-badge-text">
                Selected: {selectedElement.selector}
              </span>
              <button
                type="button"
                className="ai-chat-badge-clear"
                onClick={clearSelectedElement}
              >
                ✕
              </button>
            </div>
          )}
          {attachments.length > 0 && (
            <div className="ai-chat-attachments">
              {attachments.map((image) => (
                <div key={image.id} className="ai-chat-attachment-badge">
                  <img
                    className="ai-chat-attachment-thumb"
                    src={image.previewUrl}
                    alt=""
                  />
                  <span className="ai-chat-attachment-info">
                    <span className="ai-chat-attachment-name">{image.name}</span>
                    {image.status === AttachmentStatus.UPLOADING && (
                      <span className="ai-chat-attachment-status">
                        Uploading...
                      </span>
                    )}
                    {image.status === AttachmentStatus.ERROR && (
                      <span className="ai-chat-attachment-status is-error">
                        {image.error}
                      </span>
                    )}
                  </span>
                  {image.status === AttachmentStatus.ERROR && (
                    <button
                      type="button"
                      className="ai-chat-attachment-retry"
                      onClick={() => retryAttachment(image.id)}
                    >
                      Retry
                    </button>
                  )}
                  <button
                    type="button"
                    className="ai-chat-badge-clear"
                    onClick={() => removeAttachment(image.id)}
                    aria-label={`Remove ${image.name}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            type="file"
            className="ai-chat-file-input"
            ref={fileInputRef}
            accept={ACCEPTED_IMAGE_TYPES.join(",")}
            multiple
            onChange={onFileInputChange}
          />
          <div
            className={`ai-chat-input-wrapper ${loading || isCheckingAccess ? "is-disabled" : ""}`}
          >
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="select-area-tooltip">Attach image/file</Tooltip>
              }
            >
              <button
                type="button"
                className="ai-chat-attach-btn"
                onClick={openFilePicker}
                disabled={loading || isCheckingAccess || isFull}
                aria-label="Attach image"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
            </OverlayTrigger>
            <input
              type="text"
              className="ai-chat-input"
              placeholder="What can I help with?"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPaste={onPaste}
              disabled={loading || isCheckingAccess}
            />
            <button
              type="submit"
              className="ai-chat-send-btn"
              disabled={
                loading ||
                isCheckingAccess ||
                pending ||
                (!input.trim() && !readyAttachments.length)
              }
              aria-label="Send"
            >
              <svg
                width="12"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M0.800781 5.7998L5.80078 0.799805M5.80078 0.799805L10.8008 5.7998M5.80078 0.799805V12.7998"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="select-area-tooltip">Select area to edit</Tooltip>
              }
            >
              <button
                type="button"
                className="ai-chat-attach-btn"
                onClick={onPickerClick}
                disabled={loading || isCheckingAccess}
              >
                <PickIcon />
              </button>
            </OverlayTrigger>
          </div>
        </form>
      </div>
    </>
  );
}

export default forwardRef(AIChat);
